import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Waves, Fish, Anchor, Sun, Star, MapPin, Phone, ChevronDown } from "lucide-react";
import { IMAGES, SURF_SERVICES, FISH_SERVICES, SEA_SERVICES, TESTIMONIALS, CONTACT, GOOGLE_REVIEWS_URL, GOOGLE_WRITE_REVIEW_URL } from "../mock";
import ServiceCard from "../components/ServiceCard";
import Marquee from "../components/Marquee";
import Seo from "../components/Seo";

const FloatingTile = ({ src, className, delay = 0 }) => (
  <div
    className={`absolute rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 ${className}`}
    style={{ animation: `float 6s ease-in-out ${delay}s infinite` }}
  >
    <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
  </div>
);

const GoogleG = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={`w-5 h-5 ${className}`} aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
    <path fill="#FBBC05" d="M5.85 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.45.35-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.47 1.18 4.95l3.67-2.84Z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/>
  </svg>
);

const SectionHero = ({ icon: Icon, eyebrow, title, blurb, cta, ctaLink, image, reverse = false }) => (
  <section className="relative py-20 md:py-28 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
      <div className={`${reverse ? "md:order-2" : ""} reveal`}>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold uppercase tracking-wider mb-5">
          <Icon size={14} /> {eyebrow}
        </div>
        <h2
          className="text-4xl md:text-5xl lg:text-6xl text-slate-900 mb-5 leading-tight"
          style={{ fontFamily: "'Pacifico', cursive" }}
        >
          {title}
        </h2>
        <p className="text-slate-600 text-lg mb-7 max-w-lg">{blurb}</p>
        <Link
          to={ctaLink}
          className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full transition-colors group"
        >
          {cta} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <div className={`${reverse ? "md:order-1" : ""} relative h-[480px] md:h-[560px] reveal`}>
        <div
          className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/20"
          style={{ animation: "float 8s ease-in-out infinite" }}
        >
          <img src={image[0]} alt="" className="w-full h-full object-cover" />
        </div>
        <div
          className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-orange-300/40 blur-3xl"
        />
        <div
          className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-sky-300/40 blur-3xl"
        />
      </div>
    </div>
  </section>
);

const Home = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // intersection observer reveal
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="bg-stone-50">
      <Seo
        title="Hang Zen Surf + Sea | Surf Lessons & Fishing Charters in Encinitas, San Diego"
        description="Family-owned Encinitas surf school offering private, semi-private, group and kids surf lessons, jet ski adventures, fishing charters, sunset cruises and a Nicaragua surf retreat. USCG-licensed captains, 5.0 stars on Google."
        path="/"
      />
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          fetchpriority="high"
          poster={IMAGES.hzSunset1}
          style={{ transform: `translateY(${scrollY * 0.25}px) scale(1.05)` }}
        >
          {/* Smaller file loads first for fastest playback start */}
          <source
            src="https://assets.mixkit.co/videos/38927/38927-720.mp4"
            type="video/mp4"
            media="(max-width: 768px)"
          />
          <source
            src="https://assets.mixkit.co/videos/38927/38927-1080.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/30 to-slate-900/75" />

        <div className="relative z-20 h-full flex flex-col justify-center items-center text-center px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-xs font-semibold uppercase tracking-[0.25em] mb-6">
            <Waves size={14} /> Encinitas · San Diego · SoCal
          </div>
          <h1
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white mb-4 drop-shadow-2xl"
            style={{ fontFamily: "'Pacifico', cursive", lineHeight: 1.05 }}
          >
            Surf. Fish.<br />Repeat.
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-8 drop-shadow">
            Surf lessons, fishing charters, jet ski adventures, sunset cruises
            and more — the true Southern California stoke in every session.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/book-today"
              className="px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full shadow-lg transition-colors"
            >
              Book Today
            </Link>
            <Link
              to="/surf-lessons"
              className="px-7 py-3.5 bg-white/15 hover:bg-white/25 backdrop-blur border border-white/40 text-white font-bold rounded-full transition-colors"
            >
              View Surf Lessons
            </Link>
            <Link
              to="/fishing-trips"
              className="px-7 py-3.5 bg-white/15 hover:bg-white/25 backdrop-blur border border-white/40 text-white font-bold rounded-full transition-colors"
            >
              View Fishing Trips
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white animate-bounce">
          <ChevronDown size={28} />
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-slate-900 py-3 border-y border-slate-800">
        <Marquee
          items={[
            "Surf Lessons",
            "Fishing Charters",
            "Jet Ski Adventures",
            "Sunset Cruises",
            "Whale Watching",
            "Kids Camps",
            "Nicaragua Retreats",
          ]}
        />
      </section>

      {/* Google Reviews - auto-scrolling */}
      <section className="py-14 md:py-16 bg-white overflow-hidden border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col items-center text-center gap-4 mb-8 reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold">
              Google Reviews
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} size={20} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="font-bold text-lg">5.0</span>
              <span className="text-slate-500">·</span>
              <span className="font-semibold">60+ Reviews</span>
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 text-sm font-semibold rounded-full shadow-sm transition-colors"
              >
                <GoogleG /> See all on Google
              </a>
              <a
                href={GOOGLE_WRITE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full shadow-sm transition-colors"
              >
                <Star size={14} className="fill-white" /> Leave a Review
              </a>
            </div>
          </div>
        </div>

        <div
          className="relative reveal"
          style={{
            maskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
          }}
        >
          <div
            className="flex gap-5 w-max will-change-transform py-2 px-3"
            style={{ animation: "review-marquee 90s linear infinite" }}
          >
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <article
                key={i}
                className="w-[320px] md:w-[380px] shrink-0 bg-stone-50 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-shadow border border-slate-100 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-2.5">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.color} text-white font-bold flex items-center justify-center shrink-0 text-sm`}>
                    {t.initial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm truncate">{t.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{t.handle}</div>
                  </div>
                  <GoogleG className="shrink-0" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} size={13} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-[11px] text-slate-500">{t.when}</span>
                </div>
                <p className="text-slate-700 text-[13px] leading-relaxed flex-1">
                  {t.text.length > 260 ? t.text.slice(0, 260).trim() + "…" : t.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center reveal">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-4">
            Welcome to Hang Zen Surf + Sea
          </div>
          <h2
            className="text-4xl md:text-5xl text-slate-900 mb-6"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Surf Lessons &amp; Fishing Charters in San Diego
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Nestled in the surfing mecca of Leucadia, Hang Zen Surf + Sea provides exceptional Encinitas surf lessons that go beyond just learning to ride waves. We craft unforgettable days on the water — whether you're catching your first waves or advancing your skills. Alongside our surf school, we offer guided fishing charters and personalized ocean experiences. Our experienced crew prioritizes safety and skill while delivering the true Southern California stoke in every session.
          </p>
        </div>
      </section>

      {/* SURF SECTION */}
      <SectionHero
        icon={Waves}
        eyebrow="Book a Surf Lesson"
        title="Hang Zen Surf"
        blurb="Exceptional Encinitas surf lessons — private, semi-private, and small group sessions tailored for all skill levels. Our skilled instructors guide you from your first pop-up to confidently riding green waves."
        cta="View Surf Lessons"
        ctaLink="/surf-lessons"
        image={[IMAGES.hzGroup2, IMAGES.hzSemi2]}
      />

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SURF_SERVICES.map((s, i) => (
            <div key={s.title} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <ServiceCard svc={s} index={i} />
            </div>
          ))}
        </div>
      </section>

      {/* FISH SECTION */}
      <div className="bg-gradient-to-b from-sky-50 to-stone-50">
        <SectionHero
          icon={Fish}
          eyebrow="Let's Go Fishing"
          title="Hang Zen Fishing Charters"
          blurb="From calm bay trips for kids to offshore tuna missions, our captains know the waters from San Diego Bay to the Coronado Islands. All tackle, bait, and licensing handled."
          cta="View Fishing Charters"
          ctaLink="/fishing-trips"
          image={[IMAGES.hzCoronado2, IMAGES.hzBay2]}
          reverse
        />

        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FISH_SERVICES.map((s, i) => (
              <div key={s.title} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <ServiceCard svc={s} index={i} />
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* SEA / ADVENTURES */}
      <SectionHero
        icon={Anchor}
        eyebrow="Ocean Adventures"
        title="Hang Zen Sea"
        blurb="Jet ski adventures, whale and dolphin watching, sunset cruises, Catalina day trips and paddle board sessions. The Pacific is your playground — we'll get you out there."
        cta="Book Your Adventure"
        ctaLink="/sunset-cruises"
        image={[IMAGES.hzHome2, IMAGES.hzHome3]}
      />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SEA_SERVICES.map((s, i) => (
            <div key={s.title} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <ServiceCard svc={s} index={i} />
            </div>
          ))}
        </div>
      </section>

      {/* Nicaragua banner */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hzNicaragua1})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
        <div className="relative max-w-5xl mx-auto px-6 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400 text-slate-900 text-xs font-bold uppercase tracking-wider mb-5">
            <Sun size={14} /> Surf Retreat
          </div>
          <h2
            className="text-4xl md:text-6xl text-white mb-4 max-w-2xl"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Nicaragua Surf Retreat
          </h2>
          <p className="text-white/90 text-lg max-w-xl mb-7">
            7 days of warm water, offshore winds, daily coaching, beachfront lodging, and uncrowded points. The ultimate reset.
          </p>
          <Link
            to="/nicaragua"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full transition-colors"
          >
            View Retreat <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Contact strip */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-10">
          <div className="reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold mb-3">Contact us Now</div>
            <h3 className="text-3xl mb-4" style={{ fontFamily: "'Pacifico', cursive" }}>
              {CONTACT.name}
            </h3>
          </div>
          <div className="reveal">
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="text-orange-400 mt-1" size={20} />
              <div>
                <div className="font-semibold text-white">Visit us</div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(CONTACT.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-300 hover:text-orange-400"
                >
                  {CONTACT.address}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="text-orange-400 mt-1" size={20} />
              <div>
                <div className="font-semibold text-white">Call us</div>
                <a href={`tel:${CONTACT.phoneRaw}`} className="text-slate-300 hover:text-orange-400">
                  {CONTACT.phone}
                </a>
              </div>
            </div>
          </div>
          <div className="reveal">
            <div className="text-sm font-semibold text-white mb-3">Hours</div>
            <ul className="space-y-1 text-sm text-slate-300">
              {CONTACT.hours.slice(0, 3).map((h) => (
                <li key={h.day} className="flex justify-between">
                  <span>{h.day}</span><span>{h.time}</span>
                </li>
              ))}
              <li className="flex justify-between">
                <span>Sun</span><span>{CONTACT.hours[6].time}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
